import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { speakText } from '../utils/audioUtils';
import { supabase } from '../supabase';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';
import '../screens/WordMeaningScreen.css';

interface WordMeaning {
  italian: string;
  bengali: string;
  english: string;
  category?: string;
}

const WordMeaningScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const [wordDictionary, setWordDictionary] = useState<WordMeaning[]>([]);
  const [loading, setLoading] = useState(true);
  // Android-only banner (native pinned by AdMob plugin)

  const content = {
    bn: {
      title: "শব্দের অর্থ",
      searchPlaceholder: "ইতালীয় শব্দ খুঁজুন...",
      back: "ফিরে যান",
      noResults: "কোন ফলাফল পাওয়া যায়নি"
    },
    it: {
      title: "Significato Parole",
      searchPlaceholder: "Cerca parola italiana...",
      back: "Indietro",
      noResults: "Nessun risultato trovato"
    },
    en: {
      title: "Word Meaning",
      searchPlaceholder: "Search Italian word...",
      back: "Back",
      noResults: "No results found"
    }
  };

  // Fallback hardcoded dictionary (used if database is empty)
  const fallbackDictionary: WordMeaning[] = [
    // A words
    { italian: 'acqua', bengali: 'পানি', english: 'water', category: 'nouns' },
    { italian: 'aereo', bengali: 'বিমান', english: 'airplane', category: 'nouns' },
    { italian: 'albergo', bengali: 'হোটেল', english: 'hotel', category: 'nouns' },
    { italian: 'albero', bengali: 'গাছ', english: 'tree', category: 'nouns' },
    { italian: 'alito', bengali: 'শ্বাস', english: 'breath', category: 'nouns' },
    { italian: 'alto', bengali: 'উঁচু', english: 'tall/high', category: 'adjectives' },
    { italian: 'amare', bengali: 'ভালোবাসা', english: 'to love', category: 'verbs' },
    { italian: 'amico', bengali: 'বন্ধু', english: 'friend', category: 'nouns' },
    { italian: 'amica', bengali: 'বন্ধু (মেয়ে)', english: 'friend (female)', category: 'nouns' },
    { italian: 'anche', bengali: 'এছাড়াও', english: 'also', category: 'common' },
    { italian: 'andare', bengali: 'যাওয়া', english: 'to go', category: 'verbs' },
    { italian: 'angolo', bengali: 'কোণ', english: 'corner', category: 'nouns' },
    { italian: 'animale', bengali: 'প্রাণী', english: 'animal', category: 'nouns' },
    { italian: 'anno', bengali: 'বছর', english: 'year', category: 'nouns' },
    { italian: 'antico', bengali: 'প্রাচীন', english: 'ancient', category: 'adjectives' },
    { italian: 'aperto', bengali: 'খোলা', english: 'open', category: 'adjectives' },
    { italian: 'appena', bengali: 'এইমাত্র', english: 'just', category: 'common' },
    { italian: 'aprire', bengali: 'খোলা', english: 'to open', category: 'verbs' },
    { italian: 'arrivare', bengali: 'পৌঁছানো', english: 'to arrive', category: 'verbs' },
    { italian: 'arrivederci', bengali: 'বিদায়', english: 'goodbye', category: 'greetings' },
    { italian: 'arte', bengali: 'শিল্প', english: 'art', category: 'nouns' },
    { italian: 'ascoltare', bengali: 'শোনা', english: 'to listen', category: 'verbs' },
    { italian: 'aspettare', bengali: 'অপেক্ষা করা', english: 'to wait', category: 'verbs' },
    { italian: 'avere', bengali: 'থাকা', english: 'to have', category: 'verbs' },
    { italian: 'auto', bengali: 'গাড়ি', english: 'car', category: 'nouns' },
    
    // B words
    { italian: 'bagno', bengali: 'স্নান', english: 'bath', category: 'nouns' },
    { italian: 'bambino', bengali: 'শিশু', english: 'baby/child', category: 'nouns' },
    { italian: 'bambina', bengali: 'শিশু (মেয়ে)', english: 'baby/child (female)', category: 'nouns' },
    { italian: 'banco', bengali: 'দোকান', english: 'shop/store', category: 'nouns' },
    { italian: 'barca', bengali: 'নৌকা', english: 'boat', category: 'nouns' },
    { italian: 'basso', bengali: 'নিচু', english: 'low/short', category: 'adjectives' },
    { italian: 'battaglia', bengali: 'যুদ্ধ', english: 'battle', category: 'nouns' },
    { italian: 'bello', bengali: 'সুন্দর', english: 'beautiful', category: 'adjectives' },
    { italian: 'bene', bengali: 'ভাল', english: 'good/well', category: 'common' },
    { italian: 'bere', bengali: 'পান করা', english: 'to drink', category: 'verbs' },
    { italian: 'bianco', bengali: 'সাদা', english: 'white', category: 'adjectives' },
    { italian: 'bicicletta', bengali: 'সাইকেল', english: 'bicycle', category: 'nouns' },
    { italian: 'biglietto', bengali: 'টিকেট', english: 'ticket', category: 'nouns' },
    { italian: 'bocca', bengali: 'মুখ', english: 'mouth', category: 'nouns' },
    { italian: 'braccio', bengali: 'বাহু', english: 'arm', category: 'nouns' },
    { italian: 'brano', bengali: 'গান', english: 'song', category: 'nouns' },
    { italian: 'bravo', bengali: 'ভাল', english: 'good/bravo', category: 'adjectives' },
    { italian: 'brutto', bengali: 'কুৎসিত', english: 'ugly', category: 'adjectives' },
    { italian: 'buonasera', bengali: 'শুভ সন্ধ্যা', english: 'good evening', category: 'greetings' },
    { italian: 'buongiorno', bengali: 'সুপ্রভাত/শুভ দিন', english: 'good morning/good day', category: 'greetings' },
    { italian: 'buonanotte', bengali: 'শুভ রাত্রি', english: 'good night', category: 'greetings' },
    { italian: 'buono', bengali: 'ভাল', english: 'good', category: 'adjectives' },
    
    // C words
    { italian: 'caffè', bengali: 'কফি', english: 'coffee', category: 'nouns' },
    { italian: 'caldo', bengali: 'গরম', english: 'hot', category: 'adjectives' },
    { italian: 'calzino', bengali: 'মোজা', english: 'sock', category: 'nouns' },
    { italian: 'camera', bengali: 'কক্ষ', english: 'room', category: 'nouns' },
    { italian: 'cane', bengali: 'কুকুর', english: 'dog', category: 'nouns' },
    { italian: 'capire', bengali: 'বোঝা', english: 'to understand', category: 'verbs' },
    { italian: 'capo', bengali: 'মাথা', english: 'head', category: 'nouns' },
    { italian: 'caro', bengali: 'প্রিয়/দামী', english: 'dear/expensive', category: 'adjectives' },
    { italian: 'carta', bengali: 'কাগজ', english: 'paper', category: 'nouns' },
    { italian: 'casa', bengali: 'বাড়ি', english: 'house/home', category: 'nouns' },
    { italian: 'cattivo', bengali: 'খারাপ', english: 'bad', category: 'adjectives' },
    { italian: 'cavallo', bengali: 'ঘোড়া', english: 'horse', category: 'nouns' },
    { italian: 'cena', bengali: 'রাতের খাবার', english: 'dinner', category: 'nouns' },
    { italian: 'chiave', bengali: 'চাবি', english: 'key', category: 'nouns' },
    { italian: 'chiudere', bengali: 'বন্ধ করা', english: 'to close', category: 'verbs' },
    { italian: 'ciao', bengali: 'হ্যালো/বিদায়', english: 'hello/goodbye', category: 'greetings' },
    { italian: 'cinema', bengali: 'সিনেমা', english: 'cinema', category: 'nouns' },
    { italian: 'città', bengali: 'শহর', english: 'city', category: 'nouns' },
    { italian: 'colore', bengali: 'রঙ', english: 'color', category: 'nouns' },
    { italian: 'come', bengali: 'কীভাবে/কেমন', english: 'how', category: 'common' },
    { italian: 'come stai?', bengali: 'তুমি কেমন আছ?', english: 'how are you?', category: 'greetings' },
    { italian: 'comprare', bengali: 'কেনা', english: 'to buy', category: 'verbs' },
    { italian: 'con', bengali: 'সাথে', english: 'with', category: 'common' },
    { italian: 'cosa', bengali: 'কী', english: 'what', category: 'common' },
    { italian: 'cucina', bengali: 'রান্নাঘর', english: 'kitchen', category: 'nouns' },
    { italian: 'cuore', bengali: 'হৃদয়', english: 'heart', category: 'nouns' },
    
    // D words
    { italian: 'dare', bengali: 'দেওয়া', english: 'to give', category: 'verbs' },
    { italian: 'dente', bengali: 'দাঁত', english: 'tooth', category: 'nouns' },
    { italian: 'dentro', bengali: 'ভিতরে', english: 'inside', category: 'common' },
    { italian: 'destra', bengali: 'ডান', english: 'right', category: 'nouns' },
    { italian: 'diavolo', bengali: 'শয়তান', english: 'devil', category: 'nouns' },
    { italian: 'dimenticare', bengali: 'ভুলে যাওয়া', english: 'to forget', category: 'verbs' },
    { italian: 'dire', bengali: 'বলা', english: 'to say', category: 'verbs' },
    { italian: 'dito', bengali: 'আঙুল', english: 'finger', category: 'nouns' },
    { italian: 'dolce', bengali: 'মিষ্টি', english: 'sweet', category: 'adjectives' },
    { italian: 'domani', bengali: 'আগামীকাল', english: 'tomorrow', category: 'time' },
    { italian: 'donna', bengali: 'নারী', english: 'woman', category: 'nouns' },
    { italian: 'dopo', bengali: 'পরে', english: 'after', category: 'common' },
    { italian: 'dormire', bengali: 'ঘুমানো', english: 'to sleep', category: 'verbs' },
    { italian: 'dove', bengali: 'কোথায়', english: 'where', category: 'common' },
    { italian: 'dritto', bengali: 'সোজা', english: 'straight', category: 'adjectives' },
    
    // E words
    { italian: 'e', bengali: 'এবং', english: 'and', category: 'common' },
    { italian: 'erba', bengali: 'ঘাস', english: 'grass', category: 'nouns' },
    { italian: 'essere', bengali: 'হওয়া', english: 'to be', category: 'verbs' },
    { italian: 'estate', bengali: 'গ্রীষ্ম', english: 'summer', category: 'nouns' },
    { italian: 'età', bengali: 'বয়স', english: 'age', category: 'nouns' },
    { italian: 'euro', bengali: 'ইউরো', english: 'euro', category: 'nouns' },
    { italian: 'evitare', bengali: 'এড়ানো', english: 'to avoid', category: 'verbs' },
    
    // F words
    { italian: 'facile', bengali: 'সহজ', english: 'easy', category: 'adjectives' },
    { italian: 'fame', bengali: 'ক্ষুধা', english: 'hunger', category: 'nouns' },
    { italian: 'famiglia', bengali: 'পরিবার', english: 'family', category: 'nouns' },
    { italian: 'fare', bengali: 'করা', english: 'to do/make', category: 'verbs' },
    { italian: 'fatto', bengali: 'কাজ', english: 'fact/deed', category: 'nouns' },
    { italian: 'felice', bengali: 'খুশি', english: 'happy', category: 'adjectives' },
    { italian: 'fermare', bengali: 'থামানো', english: 'to stop', category: 'verbs' },
    { italian: 'fermo', bengali: 'স্থির', english: 'still/stopped', category: 'adjectives' },
    { italian: 'figlio', bengali: 'ছেলে', english: 'son', category: 'nouns' },
    { italian: 'figlia', bengali: 'মেয়ে', english: 'daughter', category: 'nouns' },
    { italian: 'fino', bengali: 'পর্যন্ত', english: 'until', category: 'common' },
    { italian: 'finestra', bengali: 'জানালা', english: 'window', category: 'nouns' },
    { italian: 'fiume', bengali: 'নদী', english: 'river', category: 'nouns' },
    { italian: 'fiore', bengali: 'ফুল', english: 'flower', category: 'nouns' },
    { italian: 'foglia', bengali: 'পাতা', english: 'leaf', category: 'nouns' },
    { italian: 'formaggio', bengali: 'পনির', english: 'cheese', category: 'nouns' },
    { italian: 'freddo', bengali: 'ঠান্ডা', english: 'cold', category: 'adjectives' },
    { italian: 'frutta', bengali: 'ফল', english: 'fruit', category: 'nouns' },
    { italian: 'fuoco', bengali: 'আগুন', english: 'fire', category: 'nouns' },
    { italian: 'fuori', bengali: 'বাইরে', english: 'outside', category: 'common' },
    
    // G words
    { italian: 'gamba', bengali: 'পা', english: 'leg', category: 'nouns' },
    { italian: 'gatto', bengali: 'বিড়াল', english: 'cat', category: 'nouns' },
    { italian: 'gelato', bengali: 'আইসক্রিম', english: 'ice cream', category: 'nouns' },
    { italian: 'gentile', bengali: 'ভদ্র', english: 'kind', category: 'adjectives' },
    { italian: 'gioco', bengali: 'খেলা', english: 'game', category: 'nouns' },
    { italian: 'giovane', bengali: 'তরুণ', english: 'young', category: 'adjectives' },
    { italian: 'giovani', bengali: 'তরুণরা', english: 'young people', category: 'nouns' },
    { italian: 'girare', bengali: 'ঘুরানো', english: 'to turn', category: 'verbs' },
    { italian: 'giro', bengali: 'ঘুরা', english: 'turn/round', category: 'nouns' },
    { italian: 'giorno', bengali: 'দিন', english: 'day', category: 'nouns' },
    { italian: 'gola', bengali: 'গলা', english: 'throat', category: 'nouns' },
    { italian: 'grande', bengali: 'বড়', english: 'big/large', category: 'adjectives' },
    { italian: 'grazie', bengali: 'ধন্যবাদ', english: 'thank you', category: 'greetings' },
    { italian: 'grigio', bengali: 'ধূসর', english: 'gray', category: 'adjectives' },
    { italian: 'guardare', bengali: 'দেখা', english: 'to watch/to look', category: 'verbs' },
    { italian: 'guerra', bengali: 'যুদ্ধ', english: 'war', category: 'nouns' },
    
    // I words
    { italian: 'inverno', bengali: 'শীত', english: 'winter', category: 'nouns' },
    { italian: 'italiano', bengali: 'ইতালীয়', english: 'Italian', category: 'nouns' },
    { italian: 'io', bengali: 'আমি', english: 'I', category: 'pronouns' },
    
    // L words
    { italian: 'lago', bengali: 'হ্রদ', english: 'lake', category: 'nouns' },
    { italian: 'lato', bengali: 'পাশ', english: 'side', category: 'nouns' },
    { italian: 'latte', bengali: 'দুধ', english: 'milk', category: 'nouns' },
    { italian: 'leggere', bengali: 'পড়া', english: 'to read', category: 'verbs' },
    { italian: 'lei', bengali: 'সে (মেয়ে)', english: 'she', category: 'pronouns' },
    { italian: 'libro', bengali: 'বই', english: 'book', category: 'nouns' },
    { italian: 'lontano', bengali: 'দূরে', english: 'far', category: 'adjectives' },
    { italian: 'loro', bengali: 'তারা', english: 'they', category: 'pronouns' },
    { italian: 'luce', bengali: 'আলো', english: 'light', category: 'nouns' },
    { italian: 'lui', bengali: 'সে', english: 'he', category: 'pronouns' },
    { italian: 'luna', bengali: 'চাঁদ', english: 'moon', category: 'nouns' },
    
    // M words
    { italian: 'macchina', bengali: 'গাড়ি', english: 'car', category: 'nouns' },
    { italian: 'madre', bengali: 'মা', english: 'mother', category: 'nouns' },
    { italian: 'male', bengali: 'খারাপ', english: 'bad', category: 'adjectives' },
    { italian: 'mamma', bengali: 'মা', english: 'mom', category: 'nouns' },
    { italian: 'mangiare', bengali: 'খাওয়া', english: 'to eat', category: 'verbs' },
    { italian: 'mare', bengali: 'সমুদ্র', english: 'sea', category: 'nouns' },
    { italian: 'medico', bengali: 'ডাক্তার', english: 'doctor', category: 'nouns' },
    { italian: 'mela', bengali: 'আপেল', english: 'apple', category: 'nouns' },
    { italian: 'mentre', bengali: 'যখন', english: 'while', category: 'common' },
    { italian: 'mio', bengali: 'আমার', english: 'my', category: 'adjectives' },
    { italian: 'montagna', bengali: 'পাহাড়', english: 'mountain', category: 'nouns' },
    { italian: 'mostrare', bengali: 'দেখানো', english: 'to show', category: 'verbs' },
    { italian: 'muovere', bengali: 'নাড়ানো', english: 'to move', category: 'verbs' },
    
    // N words
    { italian: 'naso', bengali: 'নাক', english: 'nose', category: 'nouns' },
    { italian: 'nebbia', bengali: 'কুয়াশা', english: 'fog', category: 'nouns' },
    { italian: 'nero', bengali: 'কালো', english: 'black', category: 'adjectives' },
    { italian: 'no', bengali: 'না', english: 'no', category: 'common' },
    { italian: 'noi', bengali: 'আমরা', english: 'we', category: 'pronouns' },
    { italian: 'nome', bengali: 'নাম', english: 'name', category: 'nouns' },
    { italian: 'nonna', bengali: 'দাদী', english: 'grandmother', category: 'nouns' },
    { italian: 'nonno', bengali: 'দাদা', english: 'grandfather', category: 'nouns' },
    { italian: 'nuovo', bengali: 'নতুন', english: 'new', category: 'adjectives' },
    
    // O words
    { italian: 'occhio', bengali: 'চোখ', english: 'eye', category: 'nouns' },
    { italian: 'oggi', bengali: 'আজ', english: 'today', category: 'time' },
    { italian: 'ogni', bengali: 'প্রতি', english: 'every', category: 'common' },
    { italian: 'onda', bengali: 'তরঙ্গ', english: 'wave', category: 'nouns' },
    { italian: 'ora', bengali: 'এখন', english: 'now', category: 'time' },
    { italian: 'orecchio', bengali: 'কান', english: 'ear', category: 'nouns' },
    { italian: 'ospedale', bengali: 'হাসপাতাল', english: 'hospital', category: 'nouns' },
    { italian: 'ospite', bengali: 'অতিথি', english: 'guest', category: 'nouns' },
    { italian: 'otto', bengali: 'আট', english: 'eight', category: 'numbers' },
    
    // P words
    { italian: 'padre', bengali: 'বাবা', english: 'father', category: 'nouns' },
    { italian: 'pagare', bengali: 'পেমেন্ট করা', english: 'to pay', category: 'verbs' },
    { italian: 'pane', bengali: 'রুটি', english: 'bread', category: 'nouns' },
    { italian: 'papà', bengali: 'বাবা', english: 'dad', category: 'nouns' },
    { italian: 'parlare', bengali: 'কথা বলা', english: 'to speak', category: 'verbs' },
    { italian: 'parte', bengali: 'অংশ', english: 'part', category: 'nouns' },
    { italian: 'passare', bengali: 'যাওয়া', english: 'to pass', category: 'verbs' },
    { italian: 'pasta', bengali: 'পাস্তা', english: 'pasta', category: 'nouns' },
    { italian: 'pasto', bengali: 'খাবার', english: 'meal', category: 'nouns' },
    { italian: 'per favore', bengali: 'দয়া করে', english: 'please', category: 'common' },
    { italian: 'perdere', bengali: 'হারানো', english: 'to lose', category: 'verbs' },
    { italian: 'pesce', bengali: 'মাছ', english: 'fish', category: 'nouns' },
    { italian: 'piano', bengali: 'আস্তে', english: 'slow/soft', category: 'adjectives' },
    { italian: 'piazza', bengali: 'চত্বর', english: 'square', category: 'nouns' },
    { italian: 'piccolo', bengali: 'ছোট', english: 'small', category: 'adjectives' },
    { italian: 'più', bengali: 'আরও', english: 'more', category: 'common' },
    { italian: 'porta', bengali: 'দরজা', english: 'door', category: 'nouns' },
    { italian: 'portare', bengali: 'নিয়ে যাওয়া', english: 'to bring', category: 'verbs' },
    { italian: 'prego', bengali: 'আপনাকে স্বাগতম/দয়া করে', english: 'you are welcome/please', category: 'greetings' },
    { italian: 'presto', bengali: 'শীঘ্র', english: 'soon', category: 'common' },
    
    // Q words
    { italian: 'qua', bengali: 'এখানে', english: 'here', category: 'common' },
    { italian: 'quale', bengali: 'কোন', english: 'which', category: 'common' },
    { italian: 'quando', bengali: 'কখন', english: 'when', category: 'common' },
    { italian: 'quanto', bengali: 'কত', english: 'how much', category: 'common' },
    { italian: 'quarto', bengali: 'চতুর্থ', english: 'fourth', category: 'numbers' },
    { italian: 'quello', bengali: 'সে', english: 'that', category: 'adjectives' },
    { italian: 'questo', bengali: 'এই', english: 'this', category: 'adjectives' },
    
    // R words
    { italian: 'ragazzo', bengali: 'ছেলে', english: 'boy', category: 'nouns' },
    { italian: 'ragazza', bengali: 'মেয়ে', english: 'girl', category: 'nouns' },
    { italian: 'restare', bengali: 'থাকা', english: 'to stay', category: 'verbs' },
    { italian: 'ricordare', bengali: 'মনে করা', english: 'to remember', category: 'verbs' },
    { italian: 'ridere', bengali: 'হাসা', english: 'to laugh', category: 'verbs' },
    { italian: 'rimanere', bengali: 'থাকা', english: 'to remain', category: 'verbs' },
    { italian: 'ristorante', bengali: 'রেস্তোরাঁ', english: 'restaurant', category: 'nouns' },
    
    // S words
    { italian: 'sale', bengali: 'নুন', english: 'salt', category: 'nouns' },
    { italian: 'salire', bengali: 'ওঠা', english: 'to go up', category: 'verbs' },
    { italian: 'sapere', bengali: 'জানা', english: 'to know', category: 'verbs' },
    { italian: 'scendere', bengali: 'নামা', english: 'to go down', category: 'verbs' },
    { italian: 'scrivere', bengali: 'লেখা', english: 'to write', category: 'verbs' },
    { italian: 'scuola', bengali: 'স্কুল', english: 'school', category: 'nouns' },
    { italian: 'scusa', bengali: 'ক্ষমা করুন', english: 'excuse me/sorry', category: 'common' },
    { italian: 'sedere', bengali: 'বসা', english: 'to sit', category: 'verbs' },
    { italian: 'sentire', bengali: 'শোনা', english: 'to hear', category: 'verbs' },
    { italian: 'sì', bengali: 'হ্যাঁ', english: 'yes', category: 'common' },
    { italian: 'sole', bengali: 'সূর্য', english: 'sun', category: 'nouns' },
    { italian: 'sorella', bengali: 'বোন', english: 'sister', category: 'nouns' },
    { italian: 'sotto', bengali: 'নিচে', english: 'under', category: 'common' },
    { italian: 'spalla', bengali: 'কাঁধ', english: 'shoulder', category: 'nouns' },
    { italian: 'spegnere', bengali: 'বন্ধ করা', english: 'to turn off', category: 'verbs' },
    { italian: 'stella', bengali: 'তারা', english: 'star', category: 'nouns' },
    { italian: 'stesso', bengali: 'একই', english: 'same', category: 'adjectives' },
    { italian: 'strada', bengali: 'রাস্তা', english: 'street/road', category: 'nouns' },
    { italian: 'studiare', bengali: 'পড়াশোনা করা', english: 'to study', category: 'verbs' },
    { italian: 'su', bengali: 'উপর', english: 'on/up', category: 'common' },
    { italian: 'sud', bengali: 'দক্ষিণ', english: 'south', category: 'nouns' },
    { italian: 'sole', bengali: 'সূর্য', english: 'sun', category: 'nouns' },
    
    // T words
    { italian: 'tagliare', bengali: 'কাটা', english: 'to cut', category: 'verbs' },
    { italian: 'tavolo', bengali: 'টেবিল', english: 'table', category: 'nouns' },
    { italian: 'tempo', bengali: 'সময়', english: 'time', category: 'nouns' },
    { italian: 'terra', bengali: 'মাটি', english: 'earth/ground', category: 'nouns' },
    { italian: 'testa', bengali: 'মাথা', english: 'head', category: 'nouns' },
    { italian: 'tra', bengali: 'মধ্যে', english: 'between', category: 'common' },
    { italian: 'tu', bengali: 'তুমি', english: 'you', category: 'pronouns' },
    { italian: 'tutto', bengali: 'সব', english: 'all', category: 'adjectives' },
    
    // U words
    { italian: 'uccello', bengali: 'পাখি', english: 'bird', category: 'nouns' },
    { italian: 'udire', bengali: 'শোনা', english: 'to hear', category: 'verbs' },
    { italian: 'ultimo', bengali: 'শেষ', english: 'last', category: 'adjectives' },
    { italian: 'un', bengali: 'এক', english: 'a/an', category: 'common' },
    { italian: 'uno', bengali: 'এক', english: 'one', category: 'numbers' },
    { italian: 'uovo', bengali: 'ডিম', english: 'egg', category: 'nouns' },
    { italian: 'uomo', bengali: 'মানুষ', english: 'man', category: 'nouns' },
    { italian: 'uscire', bengali: 'বের হওয়া', english: 'to go out', category: 'verbs' },
    
    // V words
    { italian: 'vado', bengali: 'যাচ্ছি', english: 'I go', category: 'verbs' },
    { italian: 'vedere', bengali: 'দেখা', english: 'to see', category: 'verbs' },
    { italian: 'vendere', bengali: 'বিক্রি করা', english: 'to sell', category: 'verbs' },
    { italian: 'venire', bengali: 'আসা', english: 'to come', category: 'verbs' },
    { italian: 'venti', bengali: 'বিশ', english: 'twenty', category: 'numbers' },
    { italian: 'vero', bengali: 'সত্য', english: 'true', category: 'adjectives' },
    { italian: 'verde', bengali: 'সবুজ', english: 'green', category: 'adjectives' },
    { italian: 'verso', bengali: 'দিকে', english: 'towards', category: 'common' },
    { italian: 'vestito', bengali: 'পোশাক', english: 'dress', category: 'nouns' },
    { italian: 'via', bengali: 'রাস্তা', english: 'street/way', category: 'nouns' },
    { italian: 'vino', bengali: 'মদ', english: 'wine', category: 'nouns' },
    { italian: 'voce', bengali: 'কণ্ঠ', english: 'voice', category: 'nouns' },
    { italian: 'volare', bengali: 'ওড়া', english: 'to fly', category: 'verbs' },
    { italian: 'volere', bengali: 'চাওয়া', english: 'to want', category: 'verbs' },
    
    // Additional common words
    { italian: 'abbracciare', bengali: 'আলিঙ্গন করা', english: 'to hug', category: 'verbs' },
    { italian: 'aiutare', bengali: 'সাহায্য করা', english: 'to help', category: 'verbs' },
    { italian: 'amare', bengali: 'ভালোবাসা', english: 'to love', category: 'verbs' },
    { italian: 'arrabbiato', bengali: 'রাগান্বিত', english: 'angry', category: 'adjectives' },
    { italian: 'avanti', bengali: 'সামনে', english: 'forward/ahead', category: 'common' },
    { italian: 'baciare', bengali: 'চুম্বন করা', english: 'to kiss', category: 'verbs' },
    { italian: 'borsa', bengali: 'ব্যাগ', english: 'bag', category: 'nouns' },
    { italian: 'bottega', bengali: 'দোকান', english: 'shop', category: 'nouns' },
    { italian: 'calcio', bengali: 'ফুটবল', english: 'football/soccer', category: 'nouns' },
    { italian: 'camminare', bengali: 'হাঁটা', english: 'to walk', category: 'verbs' },
    { italian: 'carne', bengali: 'মাংস', english: 'meat', category: 'nouns' },
    { italian: 'cercare', bengali: 'খুঁজা', english: 'to search', category: 'verbs' },
    { italian: 'chiave', bengali: 'চাবি', english: 'key', category: 'nouns' },
    { italian: 'cielo', bengali: 'আকাশ', english: 'sky', category: 'nouns' },
    { italian: 'collina', bengali: 'পাহাড়', english: 'hill', category: 'nouns' },
    { italian: 'corsa', bengali: 'দৌড়', english: 'race/run', category: 'nouns' },
    { italian: 'correre', bengali: 'দৌড়ানো', english: 'to run', category: 'verbs' },
    { italian: 'danza', bengali: 'নাচ', english: 'dance', category: 'nouns' },
    { italian: 'danzare', bengali: 'নাচা', english: 'to dance', category: 'verbs' },
    { italian: 'difficile', bengali: 'কঠিন', english: 'difficult', category: 'adjectives' },
    { italian: 'divertente', bengali: 'মজার', english: 'funny', category: 'adjectives' },
    { italian: 'dolore', bengali: 'ব্যথা', english: 'pain', category: 'nouns' },
    { italian: 'dormire', bengali: 'ঘুমানো', english: 'to sleep', category: 'verbs' },
    { italian: 'due', bengali: 'দুই', english: 'two', category: 'numbers' },
    { italian: 'economico', bengali: 'সাশ্রয়ী', english: 'cheap', category: 'adjectives' },
    { italian: 'elefante', bengali: 'হাতি', english: 'elephant', category: 'nouns' },
    { italian: 'emozione', bengali: 'আবেগ', english: 'emotion', category: 'nouns' },
    { italian: 'errore', bengali: 'ভুল', english: 'error', category: 'nouns' },
    { italian: 'famosa', bengali: 'বিখ্যাত', english: 'famous', category: 'adjectives' },
    { italian: 'fastidioso', bengali: 'উপদ্রবকারী', english: 'annoying', category: 'adjectives' },
    { italian: 'ferro', bengali: 'লোহা', english: 'iron', category: 'nouns' },
    { italian: 'festa', bengali: 'উৎসব', english: 'party', category: 'nouns' },
    { italian: 'fidanzato', bengali: 'বাগদত্ত', english: 'fiancé', category: 'nouns' },
    { italian: 'fine', bengali: 'শেষ', english: 'end', category: 'nouns' },
    { italian: 'fino', bengali: 'পর্যন্ত', english: 'until', category: 'common' },
    { italian: 'foglia', bengali: 'পাতা', english: 'leaf', category: 'nouns' },
    { italian: 'fratello', bengali: 'ভাই', english: 'brother', category: 'nouns' },
    { italian: 'giardino', bengali: 'বাগান', english: 'garden', category: 'nouns' },
    { italian: 'giocare', bengali: 'খেলা', english: 'to play', category: 'verbs' },
    { italian: 'gioia', bengali: 'আনন্দ', english: 'joy', category: 'nouns' },
    { italian: 'giovedì', bengali: 'বৃহস্পতিবার', english: 'Thursday', category: 'time' },
    { italian: 'giusto', bengali: 'সঠিক', english: 'right/correct', category: 'adjectives' },
    { italian: 'gusto', bengali: 'স্বাদ', english: 'taste', category: 'nouns' },
    { italian: 'immagine', bengali: 'ছবি', english: 'image', category: 'nouns' },
    { italian: 'importante', bengali: 'গুরুত্বপূর্ণ', english: 'important', category: 'adjectives' },
    { italian: 'inizio', bengali: 'শুরু', english: 'beginning', category: 'nouns' },
    { italian: 'insegnante', bengali: 'শিক্ষক', english: 'teacher', category: 'nouns' },
    { italian: 'interessante', bengali: 'আকর্ষণীয়', english: 'interesting', category: 'adjectives' },
    { italian: 'lavorare', bengali: 'কাজ করা', english: 'to work', category: 'verbs' },
    { italian: 'lavoro', bengali: 'কাজ', english: 'work/job', category: 'nouns' },
    { italian: 'lavare', bengali: 'ধোয়া', english: 'to wash', category: 'verbs' },
    { italian: 'leggero', bengali: 'হালকা', english: 'light', category: 'adjectives' },
    { italian: 'lezione', bengali: 'পাঠ', english: 'lesson', category: 'nouns' },
    { italian: 'libero', bengali: 'মুক্ত', english: 'free', category: 'adjectives' },
    { italian: 'lungo', bengali: 'লম্বা', english: 'long', category: 'adjectives' },
    { italian: 'maggiore', bengali: 'বড়', english: 'bigger/major', category: 'adjectives' },
    { italian: 'mangiare', bengali: 'খাওয়া', english: 'to eat', category: 'verbs' },
    { italian: 'marito', bengali: 'স্বামী', english: 'husband', category: 'nouns' },
    { italian: 'martedì', bengali: 'মঙ্গলবার', english: 'Tuesday', category: 'time' },
    { italian: 'mele', bengali: 'আপেল', english: 'apples', category: 'nouns' },
    { italian: 'mensa', bengali: 'ক্যান্টিন', english: 'cafeteria', category: 'nouns' },
    { italian: 'mercoledì', bengali: 'বুধবার', english: 'Wednesday', category: 'time' },
    { italian: 'migliore', bengali: 'সেরা', english: 'best', category: 'adjectives' },
    { italian: 'moglie', bengali: 'স্ত্রী', english: 'wife', category: 'nouns' },
    { italian: 'nascere', bengali: 'জন্ম নেওয়া', english: 'to be born', category: 'verbs' },
    { italian: 'nato', bengali: 'জন্ম', english: 'born', category: 'adjectives' },
    { italian: 'niente', bengali: 'কিছু না', english: 'nothing', category: 'common' },
    { italian: 'nord', bengali: 'উত্তর', english: 'north', category: 'nouns' },
    { italian: 'oggi', bengali: 'আজ', english: 'today', category: 'time' },
    { italian: 'occhiali', bengali: 'চশমা', english: 'glasses', category: 'nouns' },
    { italian: 'oppure', bengali: 'অথবা', english: 'or', category: 'common' },
    { italian: 'orologio', bengali: 'ঘড়ি', english: 'clock/watch', category: 'nouns' },
    { italian: 'ospedale', bengali: 'হাসপাতাল', english: 'hospital', category: 'nouns' },
    { italian: 'pace', bengali: 'শান্তি', english: 'peace', category: 'nouns' },
    { italian: 'pancia', bengali: 'পেট', english: 'belly', category: 'nouns' },
    { italian: 'parco', bengali: 'পার্ক', english: 'park', category: 'nouns' },
    { italian: 'passeggiare', bengali: 'হাঁটা', english: 'to walk/stroll', category: 'verbs' },
    { italian: 'pasta', bengali: 'পাস্তা', english: 'pasta', category: 'nouns' },
    { italian: 'pesante', bengali: 'ভারী', english: 'heavy', category: 'adjectives' },
    { italian: 'piedi', bengali: 'পা', english: 'feet', category: 'nouns' },
    { italian: 'pieno', bengali: 'ভরা', english: 'full', category: 'adjectives' },
    { italian: 'piscina', bengali: 'সাঁতারের পুল', english: 'swimming pool', category: 'nouns' },
    { italian: 'piuma', bengali: 'পালক', english: 'feather', category: 'nouns' },
    { italian: 'pomeriggio', bengali: 'বিকাল', english: 'afternoon', category: 'time' },
    { italian: 'preferire', bengali: 'পছন্দ করা', english: 'to prefer', category: 'verbs' },
    { italian: 'prendere', bengali: 'নেওয়া', english: 'to take', category: 'verbs' },
    { italian: 'primavera', bengali: 'বসন্ত', english: 'spring', category: 'nouns' },
    { italian: 'primo', bengali: 'প্রথম', english: 'first', category: 'numbers' },
    { italian: 'pulito', bengali: 'পরিষ্কার', english: 'clean', category: 'adjectives' },
    { italian: 'quattro', bengali: 'চার', english: 'four', category: 'numbers' },
    { italian: 'ricco', bengali: 'ধনী', english: 'rich', category: 'adjectives' },
    { italian: 'ricevere', bengali: 'পাওয়া', english: 'to receive', category: 'verbs' },
    { italian: 'ridere', bengali: 'হাসা', english: 'to laugh', category: 'verbs' },
    { italian: 'ripetere', bengali: 'পুনরাবৃত্তি করা', english: 'to repeat', category: 'verbs' },
    { italian: 'rispondere', bengali: 'উত্তর দেওয়া', english: 'to answer', category: 'verbs' },
    { italian: 'risposta', bengali: 'উত্তর', english: 'answer', category: 'nouns' },
    { italian: 'rosso', bengali: 'লাল', english: 'red', category: 'adjectives' },
    { italian: 'sabato', bengali: 'শনিবার', english: 'Saturday', category: 'time' },
    { italian: 'salutare', bengali: 'শুভেচ্ছা জানানো', english: 'to greet', category: 'verbs' },
    { italian: 'sangue', bengali: 'রক্ত', english: 'blood', category: 'nouns' },
    { italian: 'sbagliato', bengali: 'ভুল', english: 'wrong', category: 'adjectives' },
    { italian: 'sbattere', bengali: 'আঘাত করা', english: 'to hit', category: 'verbs' },
    { italian: 'scendere', bengali: 'নামা', english: 'to go down', category: 'verbs' },
    { italian: 'sci', bengali: 'স্কি', english: 'ski', category: 'nouns' },
    { italian: 'secondo', bengali: 'দ্বিতীয়', english: 'second', category: 'numbers' },
    { italian: 'seguire', bengali: 'অনুসরণ করা', english: 'to follow', category: 'verbs' },
    { italian: 'senza', bengali: 'ছাড়া', english: 'without', category: 'common' },
    { italian: 'sera', bengali: 'সন্ধ্যা', english: 'evening', category: 'time' },
    { italian: 'sopra', bengali: 'উপরে', english: 'above/over', category: 'common' },
    { italian: 'sperare', bengali: 'আশা করা', english: 'to hope', category: 'verbs' },
    { italian: 'sport', bengali: 'খেলা', english: 'sport', category: 'nouns' },
    { italian: 'squisito', bengali: 'চমৎকার', english: 'exquisite/delicious', category: 'adjectives' },
    { italian: 'stare', bengali: 'থাকা', english: 'to be/stay', category: 'verbs' },
    { italian: 'stesso', bengali: 'একই', english: 'same', category: 'adjectives' },
    { italian: 'straccio', bengali: 'ছেঁড়া কাপড়', english: 'rag', category: 'nouns' },
    { italian: 'stupendo', bengali: 'অসাধারণ', english: 'wonderful', category: 'adjectives' },
    { italian: 'suonare', bengali: 'বাদ্যযন্ত্র বাজানো', english: 'to play (instrument)', category: 'verbs' },
    { italian: 'tacere', bengali: 'চুপ থাকা', english: 'to be silent', category: 'verbs' },
    { italian: 'tardi', bengali: 'দেরি করে', english: 'late', category: 'adjectives' },
    { italian: 'telefono', bengali: 'টেলিফোন', english: 'telephone', category: 'nouns' },
    { italian: 'tenero', bengali: 'নরম', english: 'tender', category: 'adjectives' },
    { italian: 'terzo', bengali: 'তৃতীয়', english: 'third', category: 'numbers' },
    { italian: 'tessuto', bengali: 'কাপড়', english: 'fabric', category: 'nouns' },
    { italian: 'tirare', bengali: 'টানা', english: 'to pull', category: 'verbs' },
    { italian: 'toccare', bengali: 'স্পর্শ করা', english: 'to touch', category: 'verbs' },
    { italian: 'treno', bengali: 'ট্রেন', english: 'train', category: 'nouns' },
    { italian: 'troppo', bengali: 'খুব বেশি', english: 'too much', category: 'common' },
    { italian: 'trovare', bengali: 'খুঁজে পাওয়া', english: 'to find', category: 'verbs' },
    { italian: 'tuttavia', bengali: 'তবুও', english: 'however', category: 'common' },
    { italian: 'uccidere', bengali: 'হত্যা করা', english: 'to kill', category: 'verbs' },
    { italian: 'ultimo', bengali: 'শেষ', english: 'last', category: 'adjectives' },
    { italian: 'università', bengali: 'বিশ্ববিদ্যালয়', english: 'university', category: 'nouns' },
    { italian: 'uovo', bengali: 'ডিম', english: 'egg', category: 'nouns' },
    { italian: 'usare', bengali: 'ব্যবহার করা', english: 'to use', category: 'verbs' },
    { italian: 'uscita', bengali: 'প্রস্থান', english: 'exit', category: 'nouns' },
    { italian: 'utile', bengali: 'উপযোগী', english: 'useful', category: 'adjectives' },
    { italian: 'valigia', bengali: 'সুইটকেস', english: 'suitcase', category: 'nouns' },
    { italian: 'vendere', bengali: 'বিক্রি করা', english: 'to sell', category: 'verbs' },
    { italian: 'vendetta', bengali: 'প্রতিশোধ', english: 'revenge', category: 'nouns' },
    { italian: 'venire', bengali: 'আসা', english: 'to come', category: 'verbs' },
    { italian: 'vestire', bengali: 'পোশাক পরা', english: 'to dress', category: 'verbs' },
    { italian: 'vita', bengali: 'জীবন', english: 'life', category: 'nouns' },
    { italian: 'vivere', bengali: 'বাস করা', english: 'to live', category: 'verbs' },
    { italian: 'voce', bengali: 'কণ্ঠ', english: 'voice', category: 'nouns' },
    { italian: 'voglio', bengali: 'আমি চাই', english: 'I want', category: 'verbs' },
    { italian: 'vostro', bengali: 'আপনাদের', english: 'your (plural)', category: 'adjectives' },
    
    // Z words
    { italian: 'zaino', bengali: 'ব্যাকপ্যাক', english: 'backpack', category: 'nouns' },
    { italian: 'zero', bengali: 'শূন্য', english: 'zero', category: 'numbers' },
    { italian: 'zia', bengali: 'খালা/মাসি', english: 'aunt', category: 'nouns' },
    { italian: 'zio', bengali: 'চাচা/মামা', english: 'uncle', category: 'nouns' },
    { italian: 'zona', bengali: 'অঞ্চল', english: 'zone/area', category: 'nouns' },
    { italian: 'zucchero', bengali: 'চিনি', english: 'sugar', category: 'nouns' },
    { italian: 'zuppa', bengali: 'স্যুপ', english: 'soup', category: 'nouns' }
  ];

  // Load word meanings from database
  useEffect(() => {
    const loadWordMeanings = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('word_meanings')
          .select('*')
          .order('italian', { ascending: true });

        if (error) {
          console.error('Error loading word meanings:', error);
          // If table doesn't exist or error, use fallback
          setWordDictionary(fallbackDictionary);
        } else if (data && data.length > 0) {
          // Use database data
          setWordDictionary(data.map(item => ({
            italian: item.italian,
            bengali: item.bengali,
            english: item.english,
            category: item.category || undefined
          })));
        } else {
          // Database is empty, use fallback
          setWordDictionary(fallbackDictionary);
        }
      } catch (error) {
        console.error('Error loading word meanings:', error);
        setWordDictionary(fallbackDictionary);
      } finally {
        setLoading(false);
      }
    };

    loadWordMeanings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredWords = wordDictionary.filter(word =>
    word.italian.toLowerCase().includes(searchQuery.toLowerCase()) ||
    word.bengali.includes(searchQuery) ||
    word.english.toLowerCase().includes(searchQuery.toLowerCase())
  );


  const handleWordClick = async (word: string) => {
    setSelectedWord(word);
    try {
      await speakText(word, 'answer');
    } catch (error) {
      console.error('Error speaking:', error);
    }
    setTimeout(() => {
      setSelectedWord(null);
    }, 500);
  };

  return (
    <div className="word-meaning-screen">
      {/* Header */}
      <div className="screen-header">
        <button 
          className="header-btn back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="header-center">
          <h1>{content[currentLanguage].title}</h1>
        </div>
        <button 
          className="header-btn settings-btn"
          onClick={() => navigate('/settings')}
          aria-label="Open settings"
        >
          ⋯
        </button>
      </div>

      {/* Banner Ad — same as lesson page, placed under header */}
      <LessonBannerAd />

      {/* Search Bar */}
      <div className="word-meaning-search">
        <div className="search-input-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder={content[currentLanguage].searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Word List */}
      <div className="word-meaning-container">
        {loading ? (
          <div className="no-results">
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
              <p>Loading words...</p>
            </div>
          </div>
        ) : filteredWords.length === 0 ? (
          <div className="no-results">
            {content[currentLanguage].noResults}
          </div>
        ) : (
          <div className="word-meaning-list">
            {filteredWords.map((word, index) => (
              <div
                key={index}
                data-index={index}
                className={`word-meaning-item ${selectedWord === word.italian ? 'selected' : ''}`}
                onClick={() => handleWordClick(word.italian)}
              >
                <div className="word-meaning-header">
                  <span className="word-italian">{word.italian}</span>
                  <span className="word-category">{word.category}</span>
                </div>
                <div className="word-meaning-translations">
                  {currentLanguage === 'en' ? (
                    <span className="word-english">{word.english}</span>
                  ) : (
                    <span className="word-bengali">{word.bengali}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default WordMeaningScreen;

